import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Container, Wrench, Clock, Shield, Camera, Truck, MapPin } from 'lucide-react';
import { Hero } from '../components/ui/Hero';
import { Section } from '../components/ui/Section';
import { ServiceCard } from '../components/ui/ServiceCard';
import { Stats } from '../components/ui/Stats';
import { Button } from '../components/ui/button';
import { Testimonial } from '../components/ui/Testimonial';
import { ClientLogo } from '../components/ui/ClientLogo';
import { OptimizedImage } from '../components/ui/OptimizedImage';

// Create properly typed motion components
const MotionDiv = motion.div;
const MotionH2 = motion.h2;
const MotionP = motion.p;

// Hero background image from heroImages folder
const heroImageBackground = '/assets/heroImages/pexels-albinberlin-906982.jpg';

// Carousel slides for hero section - 4 slides with 2-second interval, modern thin font inspired by dahnay.com
const heroCarouselSlides = [
  {
    id: 1,
    image: '/assets/heroImages/pexels-albinberlin-906982.jpg',
    title: 'Your Containers, Our Commitment',
    subtitle: 'Container Terminals Services',
    description: 'Setting new standards in container yard operations with state-of-the-art facilities and unmatched transparency'
  },
  {
    id: 2,
    image: '/assets/heroImages/pexels-olliecraig1-7519247.jpg',
    title: 'Professional Container Handling',
    subtitle: 'Expert Handling & Maintenance',
    description: 'Experienced team ensuring safe and efficient container operations with advanced equipment and skilled personnel'
  },
  {
    id: 3,
    image: '/assets/heroImages/pexels-tomfisk-1427107.jpg',
    title: 'Modern Infrastructure',
    subtitle: 'Cutting-Edge Technology',
    description: 'Advanced equipment and systems for optimal container management meeting international standards'
  },
  {
    id: 4,
    image: '/assets/heroImages/pexels-tomfisk-3840441.jpg',
    title: 'Quality Assurance',
    subtitle: 'Rigorous Inspection Standards',
    description: 'Comprehensive quality checks ensuring cargo safety and compliance with international regulations'
  }
];

// Client logos using Marquee images for dual-row display
const clientLogos = [
  { src: '/assets/images/Marquee4.jpg', alt: 'Container Services' },
  { src: '/assets/images/Marquee5.png', alt: 'Port Operations' },
];

// Images for automatic cycling in About section - using WhoWA images
const aboutImages = [
  { src: '/assets/WhoWA/WhatsApp Image 2025-07-12 at 13.34.28_1dff0518.jpg', alt: 'Mega Marine Container Terminals facility', category: 'operations' },
  { src: '/assets/WhoWA/WhatsApp Image 2025-07-12 at 13.34.28_8aefa3ce.jpg', alt: 'Container handling operations', category: 'equipment' },
  { src: '/assets/WhoWA/WhatsApp Image 2025-07-12 at 13.34.28_e473be4d.jpg', alt: 'Port infrastructure and operations', category: 'infrastructure' },
  { src: '/assets/WhoWA/WhatsApp Image 2025-07-12 at 13.34.30_f583df15.jpg', alt: 'Professional container services', category: 'service' },
];

export default function Home() {
  // State for cycling through images
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Auto-cycle through images every 6 seconds for smooth, relaxed transitions
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % aboutImages.length);
    }, 6000); // 6 seconds for a more premium, relaxed feel

    return () => clearInterval(interval);
  }, []);

  return (
    <div>
      <Hero
        title="Your Containers, Our Commitment"
        subtitle="Mega Marine Container Terminals — Trusted partner for container handling, storage, and maintenance services"
        imageSrc={heroImageBackground}
        useCarousel={true}
        carouselSlides={heroCarouselSlides}
        autoPlayInterval={3000}
      />
      
      {/* Enhanced Client Logos Dual Marquee with Scroll Velocity */}
      <Section
        title="Trusted by Industry Leaders Worldwide"
        subtitle="Our commitment to excellence has earned partnerships with global shipping and logistics leaders"
        className="py-16 sm:py-20 md:py-24 bg-gradient-to-br from-slate-50 via-white to-slate-50 border-y border-slate-200"
        withBackground={false}
      >
        <div className="mb-8 sm:mb-10 text-center px-4">
          <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Building lasting partnerships with industry leaders across the globe through transparent operations, 
            quality service delivery, and innovative container terminals solutions
          </p>
          <Link to="/clients" className="inline-block mt-4 sm:mt-5 text-sm sm:text-base text-ocean hover:text-ocean-dark font-medium transition-colors">
            Discover our complete partner network →
          </Link>
        </div>
        <div className="px-4 sm:px-6 md:px-8">
          <ClientLogo logos={clientLogos} />
        </div>
      </Section>
      
      {/* About Section */}
      <Section
        title="Setting New Standards in Container Yard Operations"
        subtitle="Mega Marine Container Terminals offers comprehensive solutions built on transparency, quality, and trust."
        withPattern={true}
      >
        <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
          <MotionDiv
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="order-2 md:order-1"
          >
            <h3 className="text-xl sm:text-2xl font-bold mb-4 md:mb-6 text-primary">Who We Are</h3>
            <p className="text-sm sm:text-base text-gray-700 dark:text-gray-300 mb-6 md:mb-8 leading-relaxed">
              At Mega Marine Container Terminals, we've built our reputation on honesty and 
              excellence in container yard operations. With decades of experience, we pride ourselves 
              on offering transparent services that reduce risk and build trust.
            </p>
            <div className="flex gap-3 md:gap-5">
              <div className="text-primary mt-1 flex-shrink-0">
                <Container className="h-4 w-4 sm:h-5 sm:w-5" />
              </div>
              <div>
                <h4 className="font-semibold mb-1 text-sm sm:text-base">Strategic Locations</h4>
                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Conveniently located near major ports in Kandla and Mundra</p>
              </div>
            </div>
            <div className="flex gap-3 md:gap-5">
              <div className="text-primary mt-1 flex-shrink-0">
                <Shield className="h-4 w-4 sm:h-5 sm:w-5" />
              </div>
              <div>
                <h4 className="font-semibold mb-1 text-sm sm:text-base">Quality Assurance</h4>
                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Rigorous quality checks and maintenance standards</p>
              </div>
            </div>
            <div className="flex gap-3 md:gap-5">
              <div className="text-primary mt-1 flex-shrink-0">
                <Clock className="h-4 w-4 sm:h-5 sm:w-5" />
              </div>
              <div>
                <h4 className="font-semibold mb-1 text-sm sm:text-base">24/7 Operations</h4>
                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Round-the-clock service to meet your schedule</p>
              </div>
            </div>
          </MotionDiv>
          
          <MotionDiv
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="relative order-1 md:order-2 mb-8 md:mb-0"
          >
            {/* Smooth dissolve image container - optimized for performance */}
            <div className="relative group">
              <div className="relative w-full h-[340px] sm:h-[420px] md:h-[480px] bg-white rounded-3xl shadow-lg overflow-hidden border border-gray-200">
                <MotionDiv
                  key={`image-${currentImageIndex}`}
                  className="absolute inset-0 w-full h-full"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ 
                    duration: 1.0, 
                    ease: "easeInOut"
                  }}
                >
                  <OptimizedImage
                    src={aboutImages[currentImageIndex].src}
                    alt={aboutImages[currentImageIndex].alt}
                    fallbackSrc={aboutImages[0].src}
                    className="w-full h-full object-cover object-center rounded-3xl"
                    containerClassName="w-full h-full rounded-3xl overflow-hidden"
                    style={{
                      objectFit: 'cover',
                      objectPosition: 'center',
                      width: '100%',
                      height: '100%',
                      display: 'block'
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/8 to-transparent pointer-events-none" />
                </MotionDiv>
                
                {/* Minimal image indicator dots */}
                <div className="absolute bottom-4 right-4 flex space-x-1.5 z-10">
                  {aboutImages.map((_, index) => (
                    <div
                      key={index}
                      className={`w-1.5 h-1.5 rounded-full transition-all duration-500 ${
                        index === currentImageIndex 
                          ? 'bg-white/90 scale-125' 
                          : 'bg-white/40'
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </MotionDiv>
        </div>
      </Section>
      
      {/* Services Section */}
      <Section
        title="Our Services"
        subtitle="Comprehensive container handling solutions for all your shipping needs"
        className="bg-gradient-to-br from-gray-50/50 to-ocean-light/10 dark:from-primary/20 dark:to-ocean/20"
        withBackground={true}
      >
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          <ServiceCard 
            index={0}
            icon={<Container className="h-8 w-8" />}
            title="Empty Container Handling & Storage"
            description="Efficient handling and secure storage of your empty containers with our state-of-the-art equipment and spacious yard facilities."
            details={[
              "Safe loading and unloading procedures",
              "Organized stacking systems",
              "Inventory management and tracking",
              "Regular container inspection during storage"
            ]}
            imageSrc="/assets/images/EmptyContainer.jpg"
          />
          <ServiceCard 
            index={1}
            icon={<Wrench className="h-8 w-8" />}
            title="24/7 Container Repair Service"
            description="Round-the-clock repair and maintenance services for all types of containers, ensuring they meet industry standards and are ready for use."
            details={[
              "Structural repairs",
              "Panel replacement",
              "Floor repairs",
              "Door hardware servicing",
              "Specialized reefer container repairs"
            ]}
            imageSrc="/assets/images/ContainerRepair.jpg"
          />
          <ServiceCard 
            index={4}
            icon={<Truck className="h-8 w-8" />}
            title="On-site Transportation"
            description="Seamless movement of containers within our facility using specialized equipment like top lifters and hydra cranes for safe handling."
            details={[
              "Top lifter operations",
              "Hydra crane services",
              "Skilled equipment operators",
              "Regular equipment maintenance",
              "Safe container positioning"
            ]}
            imageSrc="/assets/images/OnSiteTransportation.jpg"
          />
          <ServiceCard 
            index={2}
            icon={<Shield className="h-8 w-8" />}
            title="Container Survey & Reporting"
            description="Comprehensive inspection and detailed reporting on container conditions to help you make informed decisions about repairs and maintenance."
            details={[
              "Pre-lease inspections",
              "Damage assessment",
              "Detailed photographic documentation",
              "Cost estimation for repairs",
              "Digital reports available 24/7"
            ]}
            imageSrc="/assets/images/ContainerSurvey.jpg"
          />
          <ServiceCard 
            index={3}
            icon={<Clock className="h-8 w-8" />}
            title="2+ Hectares Storage Space"
            description="Expansive storage area strategically located near major ports to accommodate all your container storage requirements with ease."
            details={[
              "Organized storage zones",
              "Efficient space utilization",
              "Easy access for quick retrieval",
              "Separate areas for different container types"
            ]}
            imageSrc="/assets/images/StorageSpace.jpg"
          />
          <ServiceCard 
            index={5}
            icon={<Camera className="h-8 w-8" />}
            title="24x7 CCTV Surveillance"
            description="Complete security with continuous monitoring of the entire yard premises to ensure the safety of your valuable assets."
            details={[
              "High-definition camera coverage",
              "24/7 monitoring",
              "Secured footage storage",
              "Regular security patrols",
              "Controlled access to premises"
            ]}
            imageSrc="/assets/images/CCTV.jpg"
          />
        </div>
        <div className="mt-12 text-center">
          <Button asChild size="lg">
            <Link to="/services" className="flex items-center gap-2 mx-auto">
              View All Services <ArrowRight className="h-5 w-5" />
            </Link>
          </Button>
        </div>
      </Section>      {/* Stats Section */}
      <section className="bg-gradient-to-br from-primary via-primary-light to-navy text-white relative overflow-hidden py-20">
        {/* Subtle background pattern */}
        <div className="absolute inset-0 opacity-5">
          <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="stats-pattern" width="40" height="40" patternUnits="userSpaceOnUse">
                <circle cx="2" cy="2" r="1" fill="white" fillOpacity="0.3" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#stats-pattern)" />
          </svg>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          {/* Custom title and subtitle with proper white colors */}
          <div className="text-center mb-16">
            <MotionH2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 text-white"
            >
              Our Excellence in Numbers
            </MotionH2>
            <MotionP
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-xl text-slate-200 max-w-3xl mx-auto"
            >
              Industry-leading performance metrics that showcase our commitment to quality and service excellence
            </MotionP>
          </div>
          <MotionDiv
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mb-12"
          >
            <Stats
              items={[
                { value: "2+", label: "Hectares Yard Area", icon: <Container className="h-8 w-8" /> },
                { value: "24/7", label: "Operations Available", icon: <Clock className="h-8 w-8" /> },
                { value: "100%", label: "Transparency Guaranteed", icon: <Shield className="h-8 w-8" /> },
                { value: "2", label: "Strategic Port Locations", icon: <MapPin className="h-8 w-8" /> },
              ]}
            />
          </MotionDiv>
            {/* Additional performance metrics */}
          <MotionDiv
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12 max-w-6xl mx-auto"
          >
            <div className="text-center px-4">
              <h4 className="text-lg font-semibold mb-2 text-blue-100">Service Quality</h4>
              <p className="text-gray-200 text-sm">Rigorous quality standards ensuring container integrity and customer satisfaction</p>
            </div>
            <div className="text-center px-4">
              <h4 className="text-lg font-semibold mb-2 text-blue-100">Safety Record</h4>
              <p className="text-gray-200 text-sm">Zero-incident operations with comprehensive safety protocols and training</p>
            </div>
            <div className="text-center px-4">
              <h4 className="text-lg font-semibold mb-2 text-blue-100">Client Satisfaction</h4>
              <p className="text-gray-200 text-sm">Long-term partnerships built on trust, reliability, and exceptional service</p>
            </div>
          </MotionDiv>
        </div>
      </section>
      
      {/* Why Choose Us */}
      <Section
        title="Why Choose Us?"
        subtitle="At Mega Marine Container Terminals, we do things differently"
        withPattern={true}
      >
        <div className="grid md:grid-cols-2 gap-8">
          <Testimonial 
            text="No hidden costs. No compromises. Just transparency, quality, and trust."
            author="Our Promise"
          />
          <Testimonial 
            text="You're choosing a partner — not just a yard. We're committed to your success in every container we handle."
            author="Our Commitment"
          />
        </div>
        <div className="mt-12 text-center">
          <Button asChild size="lg">
            <Link to="/why-choose-us" className="flex items-center gap-2 mx-auto">
              Learn What Makes Us Different <ArrowRight className="h-5 w-5" />
            </Link>
          </Button>
        </div>      </Section>      {/* CTA Section */}
      <Section className="bg-gradient-to-br from-primary via-primary-light to-navy text-white relative overflow-hidden py-20">
        {/* Subtle background pattern */}
        <div className="absolute inset-0 opacity-5">
          <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="cta-grid-pattern" width="50" height="50" patternUnits="userSpaceOnUse">
                <path d="M0 25h50M25 0v50" fill="none" stroke="white" strokeWidth="0.3" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#cta-grid-pattern)" />
          </svg>
        </div>
        
        <div className="relative z-10 text-center">
          <MotionDiv
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-6"
          >
            <Container className="h-12 w-12 mx-auto text-ocean-light mb-4" />
          </MotionDiv>
            <MotionH2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-4xl md:text-5xl lg:text-6xl font-bold mb-8 bg-gradient-to-r from-white via-ocean-light to-white bg-clip-text text-transparent leading-tight"
          >
            Partner With Mega Marine Container Terminals Today
          </MotionH2>
          
          <MotionP
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-xl md:text-2xl mb-12 text-ocean-light leading-relaxed max-w-4xl mx-auto"
          >
            Experience world-class container handling and storage solutions with industry-leading transparency and quality.
            <br className="hidden md:block" />
            <span className="text-white font-medium">Join our network of satisfied clients</span> and discover what makes us different.
          </MotionP>
          
          <MotionDiv
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-10"
          >
            <MotionDiv
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
            >
              <Button asChild size="lg" className="bg-white text-primary hover:bg-gray-50 shadow-xl border-0 rounded-full px-10 py-4 text-lg font-semibold">
                <Link to="/contact" className="flex items-center gap-3">
                  <Shield className="h-5 w-5" />
                  Get Started Today 
                  <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                </Link>
              </Button>
            </MotionDiv>
            
            <MotionDiv
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
            >
              <Button asChild size="lg" variant="outline" className="border-2 border-white/40 text-white hover:bg-white/10 backdrop-blur-sm shadow-xl rounded-full px-10 py-4 text-lg font-semibold">
                <Link to="/services" className="flex items-center gap-3">
                  <Container className="h-5 w-5" />
                  Explore Services
                </Link>
              </Button>
            </MotionDiv>
          </MotionDiv>
          
          {/* Trust indicators */}
          <MotionDiv
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="border-t border-white/20 pt-8"
          >
            <div className="grid md:grid-cols-3 gap-6 text-sm max-w-2xl mx-auto">
              <div className="flex items-center justify-center gap-2 text-blue-200">
                <Clock className="h-4 w-4" />
                <span>24/7 Operations</span>
              </div>
              <div className="flex items-center justify-center gap-2 text-blue-200">
                <Shield className="h-4 w-4" />
                <span>100% Transparency</span>
              </div>
              <div className="flex items-center justify-center gap-2 text-blue-200">
                <MapPin className="h-4 w-4" />
                <span>Strategic Locations</span>
              </div>
            </div>
          </MotionDiv>
        </div>
      </Section>
    </div>
  );
}

